import React, { ReactElement, useMemo } from 'react';
import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import { BillAPI } from '../../api';
import { Spinner } from 'react-bootstrap';

type BillTextMissingProps = {
  bill: BillDetailed;
  version: BillVersion;
};

const BillTextMissing = (props: BillTextMissingProps): ReactElement => {
  const { bill, version } = props;
  const queryArgs = useMemo(
    () => ({ billId: bill.id, versionId: version.id }),
    [bill.id, version.id],
  );
  const { data, isSuccess, isLoading } =
    BillAPI.endpoints.getBillSourceAvailability.useQuery(queryArgs);
  const billSourceAvailabilityData = (data as unknown as string) ?? '';
  const isSourceAvailable = !billSourceAvailabilityData.startsWith('Error');

  const pdfCheckStates = bill.session.state.abbreviation === 'sd';
  const pdfUrl = pdfCheckStates
    ? version.sourceDocUrl
    : `${process.env.REACT_APP_API_URL}/bills/${bill.id}/versions/${version.id}/source-document#view=FitH&amp;navpanes=0&amp;scrollbar=0`;

  // New HI sessions should now use data.capitol.hawaii domain names
  // which is NOT blocked by cloudflare
  // however some old bill data may still have the old URLs (ie does NOT include 'data')
  // hence we retain this exception but only for old HI URLs, until all are removed
  const isHICloudflareBill =
    bill.session.state.abbreviation === 'hi' &&
    !version.sourceDocUrl.includes('data.capitol.hawaii');
  const isPDFAccessible = isSuccess && isSourceAvailable && !isHICloudflareBill;

  const shouldShowPDFEmbed =
    isPDFAccessible &&
    version.sourceDocUrl &&
    version.sourceDocMediaType.includes('pdf');

  const renderErrorMessage = () => {
    if (!bill.sourceUrl && !version.sourceDocUrl) {
      return (
        <p>
          No bill text has been provided by the source. Try viewing the state or federal
          legislative website for additional information.
        </p>
      );
    } else {
      return (
        <p>
          Plural was unable to embed this bill's text from the source. View this bill on
          its{' '}
          <a href={version.sourceDocUrl} rel="noopener noreferrer" target="_blank">
            original website
          </a>{' '}
          or search for it on the{' '}
          <a href={bill.sourceUrl} rel="noopener noreferrer" target="_blank">
            original legislative website
          </a>
          .
        </p>
      );
    }
  };

  if (isLoading) {
    return <Spinner animation="border" className="my-4 mx-auto" />;
  }

  if (!shouldShowPDFEmbed) {
    return (
      <div className="bill-card card">
        <div className="card-body p-0">
          <div className="row" id="billView">
            <div className="col-12">
              <div className="bill-text" id="billText">
                {renderErrorMessage()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="source-document-embed">
        <embed height="600px" src={pdfUrl} type="application/pdf" width="100%" />
      </div>
    </div>
  );
};

export default BillTextMissing;
